import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  Field,
  Heading,
  Input,
  Label,
  Paragraph,
  Select,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { MeasureArea, ProposalError, ProposalRequest } from '../../../api/workEnvironmentTypes';
import { useAddLeaderProposalMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { FieldError, validationErrors } from '../assessmentView';
import { charactersLeft, MAX_TITLE_LENGTH, PROPOSAL_FIELDS, proposalFieldErrors } from '../measuresView';
import { areaName } from '../riskView';
import { FormErrorSummary, TextField } from '../components/FormParts';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = { surveyId: number; areas: MeasureArea[]; areaKey: string; onClose: () => void };

/** The leader's own proposal. The team sees that it came from the leader. */
export const ProposalDialog = ({ surveyId, areas, areaKey, onClose }: Props): ReactElement => {
  const { t } = useTranslation();
  const [form, setForm] = useState<ProposalRequest>({ factorKey: areaKey, title: '', reason: '' });
  const [errors, setErrors] = useState<FieldError[]>([]);
  const add = useAddLeaderProposalMutation(surveyId);
  const errorFor = (fieldId: string) => errors.find((error) => error.fieldId === fieldId)?.message;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate(form, {
      onSuccess: onClose,
      onError: (error) => {
        const invalid = validationErrors<ProposalError>(error);
        if (invalid) setErrors(proposalFieldErrors(invalid, t));
      },
    });
  };

  return (
    <Dialog open onClose={onClose} closedby="any" closeButton={t('workEnvironmentRisk.data.close')}>
      <form onSubmit={submit} noValidate className={classes.areaBody}>
        <Heading level={2} data-size="xs">
          {t('workEnvironmentMeasures.leader.addTitle')}
        </Heading>
        <Paragraph data-size="sm">{t('workEnvironmentMeasures.leader.addText')}</Paragraph>
        <FormErrorSummary errors={errors} />
        <Field data-size="sm">
          <Label htmlFor={PROPOSAL_FIELDS.area}>{t('workEnvironmentMeasures.form.area')}</Label>
          <Select
            id={PROPOSAL_FIELDS.area}
            value={form.factorKey}
            onChange={(event) => setForm({ ...form, factorKey: event.target.value })}
          >
            {areas.map((area) => (
              <Select.Option key={area.key} value={area.key}>
                {t('workEnvironmentMeasures.areaOption', {
                  name: areaName(area, t),
                  level: t(`workEnvironmentRisk.matrix.levels.${area.level}`).toLowerCase(),
                })}
              </Select.Option>
            ))}
          </Select>
        </Field>
        <Field data-size="sm">
          <Label htmlFor={PROPOSAL_FIELDS.title}>{t('workEnvironmentMeasures.form.title')}</Label>
          <Field.Description id={`${PROPOSAL_FIELDS.title}-help`}>
            {t('workEnvironmentMeasures.form.titleHelp')}
          </Field.Description>
          <Input
            id={PROPOSAL_FIELDS.title}
            value={form.title}
            maxLength={MAX_TITLE_LENGTH}
            aria-describedby={`${PROPOSAL_FIELDS.title}-help`}
            aria-invalid={Boolean(errorFor(PROPOSAL_FIELDS.title)) || undefined}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
          <Paragraph data-size="xs">
            {t('workEnvironmentMeasures.form.charactersLeft', { count: charactersLeft(form.title) })}
          </Paragraph>
          {errorFor(PROPOSAL_FIELDS.title) && <ValidationMessage>{errorFor(PROPOSAL_FIELDS.title)}</ValidationMessage>}
        </Field>
        <TextField
          id={PROPOSAL_FIELDS.reasonLength}
          label={t('workEnvironmentMeasures.form.reason')}
          value={form.reason}
          error={errorFor(PROPOSAL_FIELDS.reasonLength)}
          onChange={(reason) => setForm({ ...form, reason })}
        />
        {add.isError && !validationErrors(add.error) && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentMeasures.form.failed')}</Paragraph>
          </Alert>
        )}
        <div className={classes.dialogButtons}>
          <Button type="submit" variant="primary" data-size="sm" loading={add.isPending}>
            {t('workEnvironmentMeasures.leader.add')}
          </Button>
          <Button type="button" variant="tertiary" data-size="sm" onClick={onClose}>
            {t('workEnvironmentRisk.assessment.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
